import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../services/api';

const CHART_COLORS = ['#22d3ee', '#a78bfa', '#fb923c', '#34d399', '#f472b6', '#94a3b8'];

function Dashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [recentCases, setRecentCases] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/hdp/dashboard'),
      api.get('/hdp/cases'),
    ]).then(([dashboardRes, casesRes]) => {
      setDashboard(dashboardRes.data);
      setRecentCases((casesRes.data.cases || []).slice(0, 5));
    }).catch(console.error);
  }, []);

  const casesByStatus = useMemo(
    () => Object.entries(dashboard?.cases_by_status || {}).map(([name, value]) => ({ name, value })),
    [dashboard]
  );

  const actorsByRisk = useMemo(
    () => Object.entries(dashboard?.actors_by_risk || {}).map(([name, value]) => ({ name, value })),
    [dashboard]
  );

  const iocSplit = useMemo(() => ([
    { name: 'Observables', value: dashboard?.observables || 0 },
    { name: 'Indicators', value: dashboard?.indicators || 0 },
  ]), [dashboard]);

  return (
    <>
      <header className="main-header">
        <h1 className="page-title">Dashboard</h1>
        <div className="header-actions">
          <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
            HiveMind Deception Platform
          </span>
        </div>
      </header>

      <div className="main-body">
        <div className="card" style={{ marginBottom: 'var(--spacing-lg)' }}>
          <div className="card-body">
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
              OpenCTI-style investigation workspace — observables, indicators, actors, and organizations
              across active cases.
            </p>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Open Cases</div>
            <div className="stat-value">{dashboard?.open_cases || 0}</div>
          </div>
          <div className="stat-card cyan">
            <div className="stat-label">Observables</div>
            <div className="stat-value">{dashboard?.observables || 0}</div>
          </div>
          <div className="stat-card purple">
            <div className="stat-label">Indicators</div>
            <div className="stat-value">{dashboard?.indicators || 0}</div>
          </div>
          <div className="stat-card orange">
            <div className="stat-label">Threat Actors</div>
            <div className="stat-value">{dashboard?.high_risk_actors || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Organizations</div>
            <div className="stat-value">{dashboard?.organizations || 0}</div>
          </div>
          <div className="stat-card cyan">
            <div className="stat-label">Evidence Items</div>
            <div className="stat-value">{dashboard?.new_evidence || 0}</div>
          </div>
          <div className="stat-card purple">
            <div className="stat-label">Pending Enrichment</div>
            <div className="stat-value">{dashboard?.pending_enrichment_jobs || 0}</div>
          </div>
          <div className="stat-card orange">
            <div className="stat-label">Active Personas</div>
            <div className="stat-value">{dashboard?.active_personas || 0}</div>
          </div>
        </div>

        <div className="dashboard-grid-two" style={{ marginTop: 'var(--spacing-xl)' }}>
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Cases by status</h3>
              <Link to="/cases" className="btn btn-sm btn-secondary">Cases</Link>
            </div>
            <div className="card-body chart-card-body">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={casesByStatus}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#22d3ee" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Observables vs indicators</h3>
              <div className="header-inline-links">
                <Link to="/observables" className="btn btn-sm btn-secondary">Observables</Link>
                <Link to="/indicators" className="btn btn-sm btn-secondary">Indicators</Link>
              </div>
            </div>
            <div className="card-body chart-card-body">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={iocSplit} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90}>
                    {iocSplit.map((entry, index) => (
                      <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginTop: 'var(--spacing-lg)' }}>
          <div className="card-header">
            <h3 className="card-title">Actors by risk</h3>
            <Link to="/actors" className="btn btn-sm btn-secondary">Threat actors</Link>
          </div>
          <div className="card-body chart-card-body">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={actorsByRisk}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#a78bfa" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card" style={{ marginTop: 'var(--spacing-xl)' }}>
          <div className="card-header">
            <h3 className="card-title">Recent Cases</h3>
            <Link to="/cases" className="btn btn-sm btn-secondary">View All</Link>
          </div>
          <div className="card-body">
            {recentCases.length === 0 ? (
              <p style={{ color: 'var(--text-muted)' }}>No cases yet. <Link to="/cases">Create your first case</Link>.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Case #</th>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {recentCases.map((item) => (
                    <tr key={item.case_id}>
                      <td><code>{item.case_number}</code></td>
                      <td><Link to={`/cases/${item.case_id}`} style={{ color: 'var(--neon-cyan)' }}>{item.title}</Link></td>
                      <td>{item.status}</td>
                      <td>{item.priority}</td>
                      <td>{item.updated_at ? new Date(item.updated_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Dashboard;
